/**
 * 暖棕摩卡模板 - 详情页
 *
 * 设计理念：咖啡馆质感 + 商务杂志目录
 * - 深棕页头条 + 白色圆角正文卡
 * - 左文右信息卡两栏
 * - 图片 flex flex-col 竖状排列（规范要求）
 */

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/templates/mocha/components/Navbar";
import Footer from "@/templates/mocha/components/Footer";
import {
  getListingDetail,
  getListings,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function MochaListingDetail() {
  const { id } = useParams<{ id: string }>();
  const { siteId, loading: siteLoading } = useSite();
  const { siteConfig } = useSiteConfig();

  const [listing, setListing] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [relatedListings, setRelatedListings] = useState<ListingItem[]>([]);
  const [showPhone, setShowPhone] = useState(false);

  useEffect(() => {
    if (!id || siteLoading || !siteId) return;
    setLoading(true);
    getListingDetail(id)
      .then((data) => setListing(data))
      .catch(() => window.location.replace("/category"))
      .finally(() => setLoading(false));
  }, [id, siteId, siteLoading]);

  useEffect(() => {
    if (loading || !listing || !siteId) return;
    getListings({ page_size: 8 })
      .then((data) => {
        setRelatedListings(
          (data.list || []).filter((l) => l.id !== id).slice(0, 4)
        );
      })
      .catch(console.error);
  }, [loading, listing, id, siteId]);

  if (loading) return <LoadingSkeleton />;
  if (!listing) return null;

  const images = listing.images?.length > 0 ? listing.images : [];
  const hasContact = listing.contact_phone || listing.contact_wechat;

  return (
    <div className="min-h-screen bg-[#FAF6F0]">
      <Navbar />

      {/* 面包屑 */}
      <div className="bg-white border-b border-[#F0E6D6] pt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
          <nav className="flex items-center gap-1.5 text-xs text-[#8A7B6D]">
            <Link to="/" className="hover:text-[#C77B3F] transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-[10px]" />
            <Link to="/category" className="hover:text-[#C77B3F] transition-colors">全部分类</Link>
            {listing.category_name && (
              <>
                <i className="ri-arrow-right-s-line text-[10px]" />
                <span>{listing.category_name}</span>
              </>
            )}
            <i className="ri-arrow-right-s-line text-[10px]" />
            <span className="text-[#4A3525] truncate">{listing.title}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* ================= 左文右信息卡两栏 ================= */}
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── 左：主内容 ── */}
          <div className="lg:flex-1 min-w-0">
            {/* 页头条：深棕区块 */}
            <div className="bg-[#4A3525] rounded-lg px-6 sm:px-8 py-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-[#C77B3F]" />
              <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                {listing.is_pinned && (
                  <span className="px-2 py-0.5 bg-[#C77B3F] text-white text-[11px] font-bold rounded flex items-center gap-1">
                    <i className="ri-pushpin-line text-[10px]" />置顶
                  </span>
                )}
                {listing.category_name && (
                  <span className="px-2 py-0.5 bg-white/10 text-[#EADFCE] text-[11px] rounded border border-white/10">
                    {listing.category_name}
                  </span>
                )}
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#F5EBDD] leading-snug">{listing.title}</h1>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-xs text-[#C9B8A2]">
                <span><i className="ri-time-line mr-1" />{formatDate(listing.created_at)}</span>
                <span><i className="ri-eye-line mr-1" />{listing.view_count} 次浏览</span>
                <span><i className="ri-refresh-line mr-1" />{formatTimeSince(listing.updated_at || listing.created_at)}</span>
              </div>
            </div>

            {/* 详细介绍 */}
            <div className="bg-white rounded-lg border border-[#F0E6D6] p-6 sm:p-8 mt-5">
              <h2 className="text-base font-bold text-[#4A3525] mb-4 flex items-center gap-2">
                <span className="w-1 h-5 bg-[#C77B3F] rounded-full" />
                详细介绍
              </h2>
              <div className="text-sm text-[#2B241D] leading-relaxed whitespace-pre-wrap">
                {listing.content || "暂无详细描述。"}
              </div>
            </div>

            {/* 图片展示：flex flex-col 竖排 */}
            {images.length > 0 && (
              <div className="bg-white rounded-lg border border-[#F0E6D6] p-6 sm:p-8 mt-5">
                <h2 className="text-base font-bold text-[#4A3525] mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-[#C77B3F] rounded-full" />
                  相关图片
                  <span className="text-xs text-[#8A7B6D] font-normal">({images.length}张)</span>
                </h2>
                <div className="flex flex-col gap-4">
                  {images.map((img: string, i: number) => (
                    <div key={i} className="rounded-lg overflow-hidden border border-[#F0E6D6] bg-[#FAF6F0]">
                      <img src={img} alt={`图${i + 1}`} className="w-full h-auto object-contain" loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 安全提示 */}
            <div className="mt-5 bg-[#FAF6F0] border border-[#E8DCC8] rounded-lg px-5 py-3.5">
              <div className="flex items-start gap-2 text-xs text-[#8A7B6D]">
                <i className="ri-shield-check-line text-[#C77B3F] mt-0.5" />
                <span>交易时请核实对方资质，谨防诈骗。如发现虚假信息，请及时举报。</span>
              </div>
            </div>
          </div>

          {/* ── 右：信息卡 ── */}
          <div className="lg:w-[360px] shrink-0">
            <div className="lg:sticky lg:top-20 space-y-5">
              {/* 联系信息卡 */}
              <div className="bg-white rounded-lg border border-[#F0E6D6] overflow-hidden">
                <div className="bg-gradient-to-r from-[#C77B3F] to-[#B0672F] px-5 py-3">
                  <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                    <i className="ri-contacts-book-line" />联系信息
                  </h3>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full bg-[#FAF6F0] border border-[#E8DCC8] flex items-center justify-center text-[#C77B3F] shrink-0">
                      <i className="ri-user-3-line text-lg" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-[#2B241D]">{listing.contact_name || "匿名用户"}</p>
                      {siteConfig && listing.company_name && !siteConfig.hide_company_name && (
                        <p className="text-xs text-[#8A7B6D] mt-0.5 flex items-center gap-1 truncate">
                          <i className="ri-building-line text-[11px]" />{listing.company_name}
                        </p>
                      )}
                      {listing.address && (
                        <p className="text-xs text-[#B7A892] mt-0.5 flex items-center gap-1 truncate">
                          <i className="ri-map-pin-line text-[11px]" />{listing.address}
                        </p>
                      )}
                    </div>
                  </div>

                  {hasContact ? (
                    <div className="space-y-2.5">
                      {listing.contact_phone && (
                        showPhone ? (
                          <a href={`tel:${listing.contact_phone}`}
                            className="flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#4A3525] text-[#F5EBDD] text-sm font-semibold rounded-lg hover:bg-[#3A2A1D] transition-colors"
                          >
                            <i className="ri-phone-line" />{listing.contact_phone}
                          </a>
                        ) : (
                          <button onClick={() => setShowPhone(true)}
                            className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#4A3525] text-[#F5EBDD] text-sm font-semibold rounded-lg hover:bg-[#3A2A1D] transition-colors cursor-pointer"
                          >
                            <i className="ri-phone-line" />查看电话
                          </button>
                        )
                      )}
                      {listing.contact_wechat && (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(listing.contact_wechat).catch(() => {});
                            alert("微信号已复制：" + listing.contact_wechat);
                          }}
                          className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#FAF6F0] text-[#4A3525] text-sm font-semibold rounded-lg border border-[#C77B3F]/40 hover:bg-[#F0E6D6] transition-colors cursor-pointer"
                        >
                          <i className="ri-wechat-line" />复制微信
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-[#B7A892] text-center py-2">发布者未留下联系方式</p>
                  )}
                </div>
              </div>

              {/* 发布入口卡 */}
              <Link
                to="/publish"
                className="block bg-[#4A3525] rounded-lg p-5 text-center hover:bg-[#3A2A1D] transition-colors"
              >
                <i className="ri-add-circle-line text-[#DCA96B] text-2xl block mb-1.5" />
                <span className="text-[#F5EBDD] text-sm font-bold">发布信息</span>
              </Link>
            </div>
          </div>
        </div>

        {/* ================= 相关推荐 ================= */}
        {relatedListings.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-[#4A3525] flex items-center gap-2">
                <span className="w-1 h-5 bg-[#C77B3F] rounded-full" />
                相关推荐
              </h2>
              <Link to="/category" className="text-xs text-[#C77B3F] hover:text-[#B0672F] font-medium flex items-center gap-1">
                查看更多 <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-3">
              {relatedListings.map((item) => (
                <Link key={item.id} to={`/listing/${item.id}`}
                  className="group bg-white rounded-lg border border-[#F0E6D6] overflow-hidden hover:border-[#C77B3F]/50 hover:shadow-md transition-all w-[calc(50%-6px)] md:w-[calc(25%-9px)]"
                >
                  {/* 缩略图：aspect-square */}
                  <div className="aspect-square bg-[#FAF6F0] overflow-hidden flex items-center justify-center">
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <i className="ri-image-line text-[#DCCFB8] text-2xl" />
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-medium text-[#2B241D] line-clamp-1 group-hover:text-[#C77B3F] transition-colors">{item.title}</h3>
                    <div className="flex items-center justify-between mt-1.5 text-[11px] text-[#8A7B6D]">
                      <span>{item.category?.name || "未分类"}</span>
                      <span>{formatShortDate(item.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* 返回按钮 */}
        <div className="mt-7 text-center pb-8">
          <Link to="/category"
            className="inline-flex items-center gap-1.5 px-6 py-2.5 bg-white border border-[#E8DCC8] text-[#4A3525] text-sm rounded-lg hover:border-[#C77B3F] hover:text-[#C77B3F] transition-colors"
          >
            <i className="ri-arrow-left-line" />返回列表
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}-${d.getDate()}`;
}

function formatTimeSince(dateStr: string): string {
  const d = new Date(dateStr);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days === 0) return "今天更新";
  if (days === 1) return "昨天更新";
  if (days < 7) return `${days}天前更新`;
  return `${d.getMonth() + 1}月${d.getDate()}日更新`;
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-[#FAF6F0]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-6 animate-pulse">
        <div className="w-48 h-3 bg-[#E8DCC8] rounded mb-4" />
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="lg:flex-1 space-y-5">
            <div className="bg-white rounded-lg border border-[#F0E6D6] overflow-hidden">
              <div className="px-6 sm:px-8 pt-5 pb-4 space-y-3 bg-[#4A3525]">
                <div className="w-3/4 h-7 bg-white/15 rounded" />
                <div className="w-1/3 h-4 bg-white/10 rounded" />
              </div>
              <div className="px-6 sm:px-8 py-5 space-y-2">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="w-full h-4 bg-[#F0E6D6] rounded" />
                ))}
              </div>
            </div>
          </div>
          <div className="lg:w-[360px]">
            <div className="bg-white rounded-lg border border-[#F0E6D6] overflow-hidden">
              <div className="px-5 py-3 bg-[#C77B3F]" />
              <div className="p-5 space-y-3">
                <div className="w-32 h-5 bg-[#F0E6D6] rounded" />
                <div className="w-full h-10 bg-[#F0E6D6] rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
